
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/components/ui/sonner';

export interface StorageBucket {
  name: string;
  sizeBytes: number;
  fileCount: number;
}

export interface StorageStats {
  totalBytes: number;
  totalGB: number;
  buckets: StorageBucket[];
  available: boolean;
}

export interface AdminStats {
  totalUsers: number;
  activeUsers: number;
  totalCourses: number;
  completionRate: number;
  systemHealth: 'healthy' | 'warning' | 'critical';
  systemHealthReason?: string;
  lastBackup: string;
  storage: StorageStats;
}

export interface RecentActivity {
  id: string;
  user: string;
  action: string;
  time: string;
  type: 'completion' | 'enrollment' | 'failure' | 'achievement' | 'submission';
}

// Type interfaces for RPC responses
interface RpcResponse {
  success?: boolean;
  message?: string;
  error?: string;
}

export const adminDataService = {
  async getSystemStats(): Promise<AdminStats> {
    try {
      // Get total users count
      const { count: totalUsersCount } = await supabase
        .from('profiles')
        .select('*', { count: 'exact', head: true });

      // Get active users (users with activity in last 7 days)
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
      
      const { data: activeUserIds } = await supabase
        .from('course_assignments')
        .select('user_id')
        .gte('last_activity_date', sevenDaysAgo.toISOString());
      
      const activeUsersCount = new Set(activeUserIds?.map(a => a.user_id)).size;

      // Get course assignments for completion rate
      const { data: assignments } = await supabase
        .from('course_assignments')
        .select('status, final_grade');

      const completedAssignments = assignments?.filter(a => 
        a.status === 'completed' || (a.final_grade && a.final_grade >= 80)
      ).length || 0;
      const totalAssignments = assignments?.length || 1;
      const completionRate = (completedAssignments / totalAssignments) * 100;

      // Get real storage usage
      const storage = await this.getStorageStats();
      
      // Get real system health
      const { health, reason } = await this.getSystemHealthStatus();

      return {
        totalUsers: totalUsersCount || 0,
        activeUsers: activeUsersCount || 0,
        totalCourses: await this.getTotalCoursesCount(),
        completionRate: Math.round(completionRate * 10) / 10,
        systemHealth: health,
        systemHealthReason: reason,
        lastBackup: await this.getLastBackupTime(),
        storage
      };
    } catch (error) {
      console.error('Error fetching system stats:', error);
      throw error;
    }
  },

  async getStorageStats(): Promise<StorageStats> {
    try {
      // Try to get bucket list and sum sizes from storage buckets
      const bucketNames = ['course-audio', 'prerendered-modules', 'certificates', 'avatars'];
      const buckets: StorageBucket[] = [];
      let totalBytes = 0;
      
      for (const bucketName of bucketNames) {
        try {
          const { data: files } = await supabase.storage
            .from(bucketName)
            .list('', { limit: 1000 });
          
          if (files && files.length > 0) {
              // Estimate size based on file count and typical file sizes
              const avgSizePerFile = bucketName === 'course-audio' ? 500000 : // ~500KB for audio
                                     bucketName === 'prerendered-modules' ? 50000 : // ~50KB for modules
                                     bucketName === 'certificates' ? 100000 : // ~100KB for PDFs
                                     10000; // ~10KB for avatars
              const estimatedSize = files.length * avgSizePerFile;
              
              buckets.push({
                name: bucketName,
                sizeBytes: estimatedSize,
                fileCount: files.length
              });
              totalBytes += estimatedSize;
            }
          } catch (bucketError) {
            // Bucket might not exist or no access
            console.log(`Could not access bucket ${bucketName}:`, bucketError);
          }
        }
        
      return {
        totalBytes,
        totalGB: Math.round((totalBytes / (1024 * 1024 * 1024)) * 100) / 100,
        buckets,
        available: buckets.length > 0
      };
    } catch (error) {
      console.error('Error fetching storage stats:', error);
      return { totalBytes: 0, totalGB: 0, buckets: [], available: false };
    }
  },

  async getSystemHealthStatus(): Promise<{ health: 'healthy' | 'warning' | 'critical'; reason: string }> {
    try {
      // Check for stale operations (operations stuck in_progress for >10 min)
      const tenMinutesAgo = new Date(Date.now() - 10 * 60 * 1000).toISOString();
      
      const { count: staleEnhancementOps } = await supabase
        .from('content_enhancement_operations')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'in_progress')
        .lt('last_heartbeat', tenMinutesAgo);
      
      const { count: staleQuizOps } = await supabase
        .from('quiz_generation_operations')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'in_progress')
        .lt('last_heartbeat', tenMinutesAgo);
      
      const { count: stalePracticeOps } = await supabase
        .from('practice_generation_operations')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'in_progress')
        .lt('last_heartbeat', tenMinutesAgo);
      
      const totalStale = (staleEnhancementOps || 0) + (staleQuizOps || 0) + (stalePracticeOps || 0);
      
      // Check for recent failed operations
      const oneHourAgo = new Date(Date.now() - 60 * 60 * 1000).toISOString();
      const { count: recentFailures } = await supabase
        .from('content_enhancement_operations')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'failed')
        .gte('completed_at', oneHourAgo);
      
      // Determine health status
      if (totalStale > 5 || (recentFailures || 0) > 10) {
        return { 
          health: 'critical', 
          reason: `${totalStale} stale operations, ${recentFailures || 0} recent failures` 
        };
      }
      if (totalStale > 0 || (recentFailures || 0) > 3) {
        return { 
          health: 'warning', 
          reason: `${totalStale} stale operations, ${recentFailures || 0} recent failures` 
        };
      }
      return { health: 'healthy', reason: 'All systems operational' };
    } catch (error) {
      console.error('Error checking system health:', error);
      return { health: 'warning', reason: 'Unable to check system status' };
    }
  },

  async getRecentActivities(): Promise<RecentActivity[]> {
    try {
      const activities: RecentActivity[] = [];

      // Get recent course completions
      const { data: completions } = await supabase
        .from('course_assignments')
        .select(`
          id,
          course_id,
          completion_date,
          user_id,
          profiles!inner(name)
        `)
        .eq('status', 'completed')
        .not('completion_date', 'is', null)
        .order('completion_date', { ascending: false })
        .limit(5);

      completions?.forEach(completion => {
        activities.push({
          id: completion.id,
          user: (completion.profiles as any)?.name || 'Unknown User',
          action: `Completed ${completion.course_id}`,
          time: new Date(completion.completion_date).toLocaleString(),
          type: 'completion'
        });
      });

      // Get recent enrollments
      const { data: enrollments } = await supabase
        .from('course_assignments')
        .select(`
          id,
          course_id,
          assigned_date,
          user_id,
          profiles!inner(name)
        `)
        .order('assigned_date', { ascending: false })
        .limit(5);

      enrollments?.forEach(enrollment => {
        activities.push({
          id: enrollment.id + '_enrollment',
          user: (enrollment.profiles as any)?.name || 'Unknown User',
          action: `Started ${enrollment.course_id}`,
          time: new Date(enrollment.assigned_date).toLocaleString(),
          type: 'enrollment'
        });
      });

      // Get recent certificates
      const { data: certificates } = await supabase
        .from('certificates')
        .select(`
          id,
          course_title,
          issue_date,
          user_name
        `)
        .order('issue_date', { ascending: false })
        .limit(3);

      certificates?.forEach(cert => {
        activities.push({
          id: cert.id + '_cert',
          user: cert.user_name,
          action: `Achieved Certificate for ${cert.course_title}`,
          time: new Date(cert.issue_date).toLocaleString(),
          type: 'achievement'
        });
      });

      // Sort all activities by time and return top 10
      return activities
        .sort((a, b) => new Date(b.time).getTime() - new Date(a.time).getTime())
        .slice(0, 10);

    } catch (error) {
      console.error('Error fetching recent activities:', error);
      return [];
    }
  },

  async createUser(userData: { email: string; name: string; role: string }): Promise<boolean> {
    try {
      const { data, error } = await supabase.rpc('admin_create_user_with_role', {
        user_email: userData.email,
        user_name: userData.name,
        user_role: userData.role
      });

      if (error) throw error;
      
      // Type check the response
      const result = Array.isArray(data) ? data[0] : data;
      const typedResult = result as RpcResponse;
      
      if (typedResult?.success) {
        toast.success('User created successfully');
        return true;
      } else {
        toast.error(typedResult?.error || 'Failed to create user');
        return false;
      }
    } catch (error) {
      console.error('Error creating user:', error);
      toast.error('Failed to create user');
      return false;
    }
  },

  async updateUserRole(userId: string, newRole: string): Promise<boolean> {
    try {
      const { data, error } = await supabase.rpc('admin_update_user_role', {
        target_user_id: userId,
        new_role: newRole
      });

      if (error) throw error;
      
      // Type check the response
      const result = Array.isArray(data) ? data[0] : data;
      const typedResult = result as RpcResponse;
      
      if (typedResult?.success) {
        toast.success('User role updated successfully');
        return true;
      } else {
        toast.error(typedResult?.error || 'Failed to update user role');
        return false;
      }
    } catch (error) {
      console.error('Error updating user role:', error);
      toast.error('Failed to update user role');
      return false;
    }
  },

  async approveUser(userId: string, reviewerId: string): Promise<boolean> {
    try {
      const { data, error } = await supabase.rpc('admin_approve_user_safe', {
        target_user_id: userId,
        reviewer_id: reviewerId
      });

      if (error) throw error;
      
      // Type check the response
      const typedResult = data as RpcResponse;
      
      if (typedResult?.success) {
        toast.success('User approved successfully');
        return true;
      } else {
        toast.error(typedResult?.error || 'Failed to approve user');
        return false;
      }
    } catch (error) {
      console.error('Error approving user:', error);
      toast.error('Failed to approve user');
      return false;
    }
  },

  async bulkAssignCourses(userIds: string[], courseIds: string[], assignedBy: string): Promise<boolean> {
    try {
      const { data, error } = await supabase.rpc('admin_bulk_assign_courses_enhanced', {
        p_user_ids: userIds,
        p_course_ids: courseIds,
        p_assigned_by: assignedBy
      });

      if (error) throw error;
      
      // Type check the response
      const typedResult = data as RpcResponse;
      
      if (typedResult?.success) {
        toast.success(typedResult.message || 'Courses assigned successfully');
        return true;
      } else {
        toast.error(typedResult?.error || 'Failed to assign courses');
        return false;
      }
    } catch (error) {
      console.error('Error assigning courses:', error);
      toast.error('Failed to assign courses');
      return false;
    }
  },

  async getTotalCoursesCount(): Promise<number> {
    try {
      const { count } = await supabase
        .from('courses')
        .select('*', { count: 'exact', head: true })
        .eq('is_active', true);
      return count || 0;
    } catch (error) {
      console.error('Error getting total courses count:', error);
      return 0;
    }
  },

  async getLastBackupTime(): Promise<string> {
    try {
      const { data } = await supabase
        .from('backup_status')
        .select('completed_at')
        .eq('status', 'completed')
        .order('completed_at', { ascending: false })
        .limit(1)
        .single();
      
      if (data?.completed_at) {
        const backupTime = new Date(data.completed_at);
        const now = new Date();
        const diffHours = Math.floor((now.getTime() - backupTime.getTime()) / (1000 * 60 * 60));
        return diffHours === 0 ? 'Just now' : `${diffHours} hours ago`;
      }
      return 'Never';
    } catch (error) {
      console.error('Error getting last backup time:', error);
      return 'Unknown';
    }
  }
};
